import { useState, useMemo } from 'react'
import { X } from '@/components/icons'
import { Button } from './ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import { DutyStatus } from '@@api/shared'
import { SingleRosterEntryDrawer } from './SingleRosterEntryDrawer'
import { RosterListEntryItem } from './RosterListEntryItem'
import { useTripLegsByCrewAndDateRange, useAircraftMaintenance } from '@/lib/hooks'
import { MaintenanceDayList } from './MaintenanceDayList'
import dayjs, { Dayjs } from 'dayjs'

interface CrewInfo {
  id: string
  name: string
  firstName?: string | null
  lastName?: string | null
  imageUrl?: string | null
  avatarColor?: string | null
  crewRole?: string
}

export interface RosterEntryWithCrew {
  id: string
  date: Dayjs
  crewId: string
  dutyStatus: DutyStatus
  crew?: CrewInfo
  isPinned?: boolean
}

export interface ScheduledCrewDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  date: Date | null
  entries: RosterEntryWithCrew[]
  /** When provided, clicking a crew member calls this instead of opening SingleRosterEntryDrawer */
  onCrewClick?: (entry: RosterEntryWithCrew) => void
  /** Aircraft filter (single id narrows maintenance to that aircraft) */
  aircraftIds?: string[]
}

const dutyStatusLabel: Record<DutyStatus, string> = {
  [DutyStatus.OnDuty]: 'On Duty',
  [DutyStatus.OffDuty]: 'Off Duty',
  [DutyStatus.Training]: 'Training',
  [DutyStatus.Standby]: 'Standby',
  [DutyStatus.Leave]: 'Leave',
  [DutyStatus.Sick]: 'Sick',
  [DutyStatus.Positioning]: 'Positioning',
}

export function ScheduledCrewDrawer({
  isOpen,
  onOpenChange,
  date,
  entries,
  onCrewClick,
  aircraftIds,
}: ScheduledCrewDrawerProps) {
  // Aircraft in maintenance for this day, scoped to the current aircraft
  // filter — same lookup the calendar uses to draw the wrench.
  const { getMaintenanceForDay } = useAircraftMaintenance(aircraftIds)
  const maintenanceAircraft = date ? getMaintenanceForDay(dayjs(date)) : []
  const [selectedEntry, setSelectedEntry] = useState<RosterEntryWithCrew | null>(null)
  // Open state is separate from selectedEntry so the entry drawer can stay
  // mounted (entry kept) through its exit animation on close.
  const [entryOpen, setEntryOpen] = useState(false)

  // Get unique crew members from entries
  const uniqueCrewEntries = entries.reduce((acc, entry) => {
    if (entry.crew && !acc.find((e) => e.crewId === entry.crewId)) {
      acc.push(entry)
    }
    return acc
  }, [] as RosterEntryWithCrew[])

  const crewIds = useMemo(() => uniqueCrewEntries.map((e) => e.crewId), [uniqueCrewEntries])

  // Normalize the calendar date to UTC so "April 12" queries April 12 00:00Z–23:59Z
  const utcDate = date
    ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    : undefined

  // Fetch flight assignments for the crew on this date
  const { data: tripLegs = [] } = useTripLegsByCrewAndDateRange({
    crewIds,
    startDate: utcDate,
    endDate: utcDate,
  })

  // Build a map of crewId -> trip leg info for flights on this date
  const crewFlightMap = useMemo(() => {
    const map: Record<string, { departure: string; arrival: string; aircraft: string }> = {}
    for (const leg of tripLegs as any[]) {
      const depCode = leg.departureAirportIcao || ''
      const arrCode = leg.arrivalAirportIcao || ''
      const aircraft = leg.aircraft?.registration || ''
      for (const crew of leg.crew ?? []) {
        if (crewIds.includes(crew.id)) {
          map[crew.id] = { departure: depCode, arrival: arrCode, aircraft }
        }
      }
    }
    return map
  }, [tripLegs, crewIds])

  const handleEntryClick = (entry: RosterEntryWithCrew) => {
    if (onCrewClick) {
      onCrewClick(entry)
      return
    }
    setSelectedEntry(entry)
    setEntryOpen(true)
  }

  const handleSingleEntryClose = () => {
    setEntryOpen(false)
  }

  const handleSingleEntrySuccess = () => {
    setEntryOpen(false)
    onOpenChange(false)
  }

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
        <DrawerHeader className="flex items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {date ? dayjs(date).format('MMMM D') : 'Scheduled Crew'}
          </DrawerTitle>
          <DrawerClose asChild>
            <Button variant="secondary" size="icon" roundness="round" className="size-8">
              <X className="w-4 h-4" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        {/* Aircraft in maintenance for this day */}
        {maintenanceAircraft.length > 0 && (
          <div className="px-4 pb-4">
            <MaintenanceDayList aircraft={maintenanceAircraft} />
          </div>
        )}

        {/* Content */}
        <div className="flex flex-col gap-2 px-4 pb-4">
          {uniqueCrewEntries.length === 0 ? (
            <p className="text-sm text-content-primary-subtle py-4 text-center">
              No crew scheduled for this date
            </p>
          ) : (
            uniqueCrewEntries.map((entry) => {
              const flight = crewFlightMap[entry.crewId]
              return (
                <button key={entry.id} type="button" className="w-full">
                  <RosterListEntryItem
                    title={dutyStatusLabel[entry.dutyStatus] ?? entry.dutyStatus}
                    dutyStatus={entry.dutyStatus}
                    tripLeg={flight || null}
                  />
                </button>
              )
            })
          )}
        </div>
      </DrawerContent>

      {/* Single Roster Entry Drawer */}
      {selectedEntry && (
        <SingleRosterEntryDrawer
          open={entryOpen}
          onOpenChange={(open) => {
            if (!open) handleSingleEntryClose()
          }}
          rosterId={selectedEntry.id}
          onSuccess={handleSingleEntrySuccess}
        />
      )}
    </Drawer>
  )
}
